import React from "react";
import styled from "styled-components";
import {
  Box,
  Divider,
  Heading,
  Hidden,
  Inline,
  Text,
  TileLink,
} from "@origin-digital/ods-core";
import {
  IconChevronRightSmall,
  IconDescription,
} from "@origin-digital/ods-icons";
import type { CategoryCardProps, SubLink } from "./types";

const CategoryCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
  border-radius: 16px;

  .category-card-container:hover & {
    background-color: ${({ theme }) =>
      `${theme?.palette?.neutral?.dark ?? "#232323"}14`};
  }

  & .category-tile-link:hover {
    background-color: transparent !important;
    cursor: default;
  }
`;

const SubLinkItem = ({
  subLink,
  idPrefix,
}: {
  subLink: SubLink;
  idPrefix: string;
}) => (
  <React.Fragment>
    <TileLink
      href={subLink.href}
      dataId={`${idPrefix}-${
        subLink.href.split("/").filter(Boolean).pop() ??
        subLink.label.toLowerCase().replace(/\s+/g, "-")
      }`}
      trackingType="button"
      trackingLabel={`Article:${subLink.label}`}
      trackingData={{
        label: `Article:${subLink.label}`,
        componentType: "button",
      }}
      cardProps={{
        backgroundColor: "transparent",
        paddingXSpecific: "medium",
        paddingYSpecific: "medium",
      }}
      hideChevron
    >
      <Box
        alignItems="center"
        display="flex"
        width="full"
        justifyContent="space-between"
      >
        <Inline space="small" alignY="center">
          <IconDescription color="neutral" size="xxsmall" />
          <Text tone="neutral" weight="medium">
            {subLink.label}
          </Text>
        </Inline>
      </Box>
    </TileLink>
    <Box paddingX="small">
      <Divider />
    </Box>
  </React.Fragment>
);

export const SubLinkList = ({
  subLinks,
  idPrefix,
}: {
  subLinks: SubLink[];
  idPrefix: string;
}) => (
  <Box
    display="flex"
    width="full"
    flexDirection="column"
    paddingX={{ xs: "xsmall", sm: "medium" }}
    paddingTop="medium"
  >
    {subLinks.slice(0, 2).map((subLink) => (
      <SubLinkItem key={subLink.href} subLink={subLink} idPrefix={idPrefix} />
    ))}
  </Box>
);

const PictogramSection = ({ src }: { src: string }) => (
  <Box display="flex" width="full" marginBottom="small">
    <img src={src} width="96" height="96" alt="" />
  </Box>
);

const CategoryCardTitleRow = ({ name }: { name: string }) => (
  <Box
    display="flex"
    width="full"
    flexDirection="row"
    alignItems="center"
    justifyContent="space-between"
    gap="medium"
  >
    <Heading component="h3" variant="h2">
      {name}
    </Heading>
    <Box display="flex" position="relative" left="xxsmall">
      <IconChevronRightSmall
        variant="solid"
        size="xsmall"
        color="neutralDark"
      />
    </Box>
  </Box>
);

const CategoryCardHeader = ({
  name,
  description,
  icon,
  image,
}: {
  name: string;
  description: string;
  icon: string | null;
  image?: string | null;
}) => {
  return (
    <Box
      display="flex"
      width="full"
      flexDirection="column"
      paddingX={{ xs: "large", sm: "xlarge" }}
      height="full"
    >
      {/* Desktop (md+): show pictogram from image URL */}
      {image && (
        <Hidden below="lg">
          <PictogramSection src={image} />
        </Hidden>
      )}
      {/* Mobile (xs/sm): show icon from URL */}
      {icon && (
        <Hidden above="lg">
          <Box display="flex" width="full" marginBottom="small">
            <img src={icon} width="32" height="32" alt="" />
          </Box>
        </Hidden>
      )}
      <Box display="flex" width="full" flexDirection="column" gap="small">
        <CategoryCardTitleRow name={name} />
        <Hidden below="lg">
          <Text tone="neutralDark">{description}</Text>
        </Hidden>
      </Box>
    </Box>
  );
};

export const CategoryCard: React.FC<CategoryCardProps> = ({ category }) => (
  <Box
    display="flex"
    height="full"
    width="full"
    className="category-card-container"
    data-id={`category-card-${category.slug}`}
  >
    <CategoryCardContainer>
      <TileLink
        href={category.url}
        hideChevron
        dataId={`category-tile-${category.slug}`}
        trackingType="button"
        trackingLabel={`Category:${category.name}`}
        trackingData={{
          label: `Category:${category.name}`,
          componentType: "button",
        }}
        cardProps={{
          borderRadius: "none",
          backgroundColor: "transparent",
          paddingXSpecific: "none",
          paddingYSpecific: "none",
          className: "category-tile-link",
        }}
      >
        <Box
          display="flex"
          width="full"
          flexDirection="column"
          paddingTop="xlarge"
        >
          <CategoryCardHeader
            name={category.name}
            description={category.description}
            icon={category.icon}
            image={category.image}
          />
        </Box>
      </TileLink>
      <Box paddingBottom="xlarge" className="category-sublink-list">
        <SubLinkList
          subLinks={category.subLinks ?? []}
          idPrefix={`sub-link-${category.slug}`}
        />
      </Box>
    </CategoryCardContainer>
  </Box>
);
